import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { NewTemplateButton, TemplateRowActions } from "@/components/shifts/template-actions";
import { nav, shifts as copy } from "@/content";
import { can } from "@/constants/permissions";
import { buildQuery } from "@/utils/query-params";
import { listTemplates, templateUsage } from "@/api/shifts";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/shifts/templates";

/**
 * Shift templates — the patterns a rota is built from.
 *
 * The prototype's equivalent was two `HH:mm` string columns on the users table,
 * which meant one pattern per person, forever, and no way to express a night
 * shift's duration. A template is that concept done properly: named, reusable,
 * with midnight-crossing stated rather than inferred.
 */
export const Route = createFileRoute("/_main/dashboard/shifts/templates")({
  component: () => (
    <RequirePermission action="shifts.read">
      <TemplatesPage />
    </RequirePermission>
  ),
});

function TemplatesPage() {
  const { user } = useSession();
  const params = useQueryParams();
  const showArchived = params.archived === "1";
  const fmt = useFmt();

  const list = useQuery({
    queryKey: ["shifts", "templates", { showArchived }],
    queryFn: () => listTemplates({ includeArchived: showArchived }),
  });

  /* Usage counts as their own query. They are a small aggregate each and change
     only when a shift is created or deleted, so they are held longer than the
     template list itself — the alternative, a join with a group-by, buys nothing
     here and reads worse. */
  const usageQuery = useQuery({
    queryKey: ["shifts", "templateUsage", { showArchived }],
    queryFn: () => templateUsage({ includeArchived: showArchived }),
    staleTime: 60_000,
  });

  const templates = list.data ?? [];
  const usage = usageQuery.data ?? {};

  const canWrite = can(user.role, "shifts.write");
  const columnCount = 5 + (canWrite ? 1 : 0);

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={copy.templatesTitle}
        lead={copy.templatesLead}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/shifts"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
            >
              {/* Points the way the reader travels: "back" in RTL is rightward. */}
              <ArrowRight aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.backToRota}
            </Link>
            {canWrite ? <NewTemplateButton /> : null}
          </div>
        }
      />

      <p className="mt-5">
        <Link
          to={`${BASE_PATH}${buildQuery(params, { archived: showArchived ? null : "1" })}`}
          className="text-xs font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
        >
          {showArchived ? copy.hideArchivedTemplates : copy.showArchivedTemplates}
        </Link>
      </p>

      <TableShell className="mt-4">
        <Table>
          <THead>
            <TH>{copy.templateName}</TH>
            <TH>{copy.span}</TH>
            <TH numeric>{copy.duration}</TH>
            <TH numeric>{copy.breakMinutes}</TH>
            <TH numeric>{copy.columnUsage}</TH>
            {canWrite ? <TH srOnly>{copy.editTemplate}</TH> : null}
          </THead>

          <TBody>
            {templates.length === 0 ? (
              <TEmpty colSpan={columnCount}>
                <EmptyState
                  variant="empty"
                  title={copy.templatesEmptyTitle}
                  lead={copy.templatesEmptyLead}
                />
              </TEmpty>
            ) : (
              templates.map((template) => (
                <TR key={template.id} muted={template.archivedAt !== null}>
                  <TD>
                    <span className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="size-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: template.color }}
                      />
                      <span className="font-semibold text-n9">{template.name}</span>
                      {template.archivedAt ? (
                        <StatusPill tone="muted">{copy.archivedBadge}</StatusPill>
                      ) : null}
                    </span>
                  </TD>

                  <TD>
                    <span className="tabular text-xs" dir="ltr">
                      {fmt.clockLabel(template.startTime)}–{fmt.clockLabel(template.endTime)}
                    </span>
                    {/* The flag that makes the duration computable. Shown on the
                        row, because a night shift's span reads as backwards
                        otherwise and the operator would think it a mistake. */}
                    {template.crossesMidnight ? (
                      <span className="mt-0.5 block text-[11px] text-orange-700">
                        {copy.overnight}
                      </span>
                    ) : null}
                  </TD>

                  <TD numeric>
                    <span className="text-xs">{fmt.duration(template.paidMinutes)}</span>
                  </TD>

                  <TD numeric>
                    <span className="text-xs">{fmt.count(template.breakMinutes)}</span>
                  </TD>

                  <TD numeric>
                    {(usage[template.id]?.total ?? 0) === 0 ? (
                      <span className="text-xs text-n6">{copy.usageNone}</span>
                    ) : (
                      <span className="text-xs">
                        <span className="tabular">{fmt.count((usage[template.id]?.upcoming ?? 0))}</span>{" "}
                        {copy.usageUpcoming}
                      </span>
                    )}
                  </TD>

                  {canWrite ? (
                    <TD className="text-end">
                      <TemplateRowActions
                        template={{
                          id: template.id,
                          name: template.name,
                          startTime: template.startTime,
                          endTime: template.endTime,
                          crossesMidnight: template.crossesMidnight,
                          breakMinutes: template.breakMinutes,
                          color: template.color,
                          archivedAt: template.archivedAt,
                          upcoming: (usage[template.id]?.upcoming ?? 0),
                        }}
                        numerals={user.numeralSystem}
                      />
                    </TD>
                  ) : null}
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </TableShell>
    </div>
  );
}
